import React, { useState, useEffect } from 'react';
import { ScrollText, AlertTriangle } from 'lucide-react';
import supabase from '../lib/supabaseClient';
import { VISIBLE_TENANTS_OR } from '../lib/tenantVisibility';

const DEFAULT_MESSAGE =
  "We've made important updates to our Terms of Service. Please review and agree to continue using SurFox AI.";

export default function TosAlerts() {
  const [tenants, setTenants] = useState([]);
  const [scope, setScope] = useState('one'); // 'one' | 'all'
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name')
        .or(VISIBLE_TENANTS_OR)
        .order('name');
      if (!error && data) setTenants(data);
    })();
  }, []);

  const canSend = message.trim() && (scope === 'all' || selectedTenantId);
  const selectedTenantName = tenants.find(t => t.id === selectedTenantId)?.name;

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('send-tos-alert', {
        body: {
          tenant_ids: scope === 'all' ? 'all' : [selectedTenantId],
          message: message.trim(),
        },
      });
      if (error) {
        setResult({ ok: false, text: error.message || 'Something went wrong.' });
      } else {
        setResult({ ok: true, text: `Sent. ${data.affected} user${data.affected === 1 ? '' : 's'} will see the modal next time they load the app.` });
      }
    } catch (err) {
      setResult({ ok: false, text: err.message || 'Something went wrong.' });
    }
    setSending(false);
    setConfirming(false);
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-2 mb-1">
        <ScrollText className="w-6 h-6 text-indigo-500" />
        <h1 className="text-xl font-bold text-gray-900">TOS Alerts</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Blocks the picked tenant(s) with the Terms modal until someone there re-accepts.
        Every gated action (AI replies, sends, etc.) is refused for them until they do.
      </p>

      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Who should see this</label>
          <div className="flex gap-3 mb-3">
            <button
              onClick={() => setScope('one')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                scope === 'one' ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              One tenant
            </button>
            <button
              onClick={() => setScope('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                scope === 'all' ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              All tenants
            </button>
          </div>

          {scope === 'one' && (
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select a tenant…</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Popup message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Explain what changed…"
          />
          <p className="text-xs text-gray-400 mt-1">
            Shown under "We've Updated Our Terms of Service" in the modal, above the Terms link and checkboxes.
          </p>
        </div>

        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            disabled={!canSend}
            className="w-full py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Send TOS Alert
          </button>
        ) : (
          <div className="border border-amber-300 bg-amber-50 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                This blocks {scope === 'all' ? 'every tenant' : <strong>{selectedTenantName || 'the selected tenant'}</strong>} from using
                SurFox AI immediately, for everyone there, until they accept. Confirm?
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSend}
                disabled={sending}
                className="flex-1 py-2 rounded-lg font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Yes, send it'}
              </button>
              <button
                onClick={() => setConfirming(false)}
                disabled={sending}
                className="flex-1 py-2 rounded-lg font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {result && (
          <p className={`text-sm ${result.ok ? 'text-green-600' : 'text-red-600'}`}>{result.text}</p>
        )}
      </div>
    </div>
  );
}
